import {
  bandActivity,
  defaultParams,
  demodulate,
  frameSeconds,
  modulate,
  type CodecParams,
} from "@psplit/ledger-core";

// Speaker and microphone glue for the nearby beacon. The codec itself is pure
// and lives in core next to the QR chunker; everything platform-shaped is here.

const BEACON_GAP_MS = 700;
const DETECT_EVERY_MS = 900;
const ACTIVITY_THRESHOLD = 2.5; // on-tone vs guard-bin energy before a decode is worth the CPU

let sharedCtx: AudioContext | null = null;

/** One AudioContext for the app. Created inside a user gesture: iOS refuses otherwise. */
export function audioContext(): AudioContext {
  sharedCtx ??= new AudioContext();
  void sharedCtx.resume();
  return sharedCtx;
}

export function beaconSeconds(bytes: number): number {
  return frameSeconds(defaultParams(audioContext().sampleRate), bytes);
}

function playOnce(ac: AudioContext, p: CodecParams, payload: Uint8Array): Promise<void> {
  const samples = modulate(payload, p);
  const buffer = ac.createBuffer(1, samples.length, ac.sampleRate);
  buffer.getChannelData(0).set(samples);
  const source = ac.createBufferSource();
  source.buffer = buffer;
  source.connect(ac.destination);
  return new Promise((resolve) => {
    source.onended = () => resolve();
    source.start();
  });
}

/**
 * Play the beacon on a loop until the caller aborts. Repeating matters: the
 * other phone may not have started listening yet, and a single burst that
 * lands during a permission prompt is simply lost.
 */
export async function broadcastBeacon(payload: Uint8Array, signal: AbortSignal): Promise<void> {
  const ac = audioContext();
  const p = defaultParams(ac.sampleRate);
  while (!signal.aborted) {
    await playOnce(ac, p, payload);
    if (signal.aborted) return;
    await new Promise((r) => setTimeout(r, BEACON_GAP_MS));
  }
}

export type ListenStatus = "asking" | "listening" | "heard" | "denied" | "timeout" | "unsupported";

export interface ListenResult {
  status: ListenStatus;
  payload?: Uint8Array;
  error?: string;
}

/**
 * Listen until a frame decodes or the caller gives up. Keeps a rolling window
 * a little longer than one frame and re-runs the detector on a timer, so a
 * beacon that starts mid-window is still caught on the next pass.
 */
export async function listenForBeacon(
  expectedBytes: number,
  signal: AbortSignal,
  onStatus?: (status: ListenStatus) => void,
): Promise<ListenResult> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    onStatus?.("unsupported");
    return { status: "unsupported" };
  }

  onStatus?.("asking");
  let stream: MediaStream;
  try {
    // All three of these would eat the band alive. Echo cancellation is the
    // worst of them: it is tuned for speech and treats a steady 18 kHz tone as
    // something to subtract away.
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
      },
    });
  } catch (e) {
    onStatus?.("denied");
    return { status: "denied", error: e instanceof Error ? e.message : String(e) };
  }

  const ac = audioContext();
  const p = defaultParams(ac.sampleRate);
  const windowSamples = Math.ceil((frameSeconds(p, expectedBytes) + 1.2) * ac.sampleRate);

  let node: AudioWorkletNode;
  try {
    await ac.audioWorklet.addModule("/nearby-worklet.js");
    node = new AudioWorkletNode(ac, "psplit-tap");
  } catch (e) {
    for (const track of stream.getTracks()) track.stop();
    onStatus?.("unsupported");
    return { status: "unsupported", error: e instanceof Error ? e.message : String(e) };
  }

  const source = ac.createMediaStreamSource(stream);
  const ring = new Float32Array(windowSamples);
  let filled = 0;

  node.port.onmessage = (event: MessageEvent<Float32Array>) => {
    const chunk = event.data;
    if (chunk.length >= ring.length) {
      ring.set(chunk.subarray(chunk.length - ring.length));
      filled = ring.length;
      return;
    }
    ring.copyWithin(0, chunk.length);
    ring.set(chunk, ring.length - chunk.length);
    filled = Math.min(ring.length, filled + chunk.length);
  };
  source.connect(node);
  // Not connected to destination: this is a tap, and routing a live mic to the
  // speaker is how you get feedback howl.

  const cleanup = () => {
    node.port.onmessage = null;
    source.disconnect();
    node.disconnect();
    for (const track of stream.getTracks()) track.stop();
  };

  onStatus?.("listening");
  try {
    while (!signal.aborted) {
      await new Promise((r) => setTimeout(r, DETECT_EVERY_MS));
      if (signal.aborted) break;
      if (filled < ring.length) continue;
      const window = ring.slice();
      if (bandActivity(window, p) < ACTIVITY_THRESHOLD) continue;
      const result = demodulate(window, p);
      if (result.ok && result.payload.length === expectedBytes) {
        onStatus?.("heard");
        return { status: "heard", payload: result.payload };
      }
    }
    onStatus?.("timeout");
    return { status: "timeout" };
  } finally {
    cleanup();
  }
}
